import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { fetchAccessGroups } from '../../api/accessGroups';

/**
 * Top navigation bar. Shows Log In / Sign Up when logged out, and the
 * username + Log Out when logged in. `onOpenAuth(view)` opens the side panel.
 */
export default function Nav({ onOpenAuth }) {
  const { user, isAuthenticated, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [allGroups, setAllGroups] = useState([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the mobile menu whenever auth state changes (e.g. after login/logout).
  useEffect(() => {
    setMenuOpen(false);
    setDropdownOpen(false);
  }, [isAuthenticated]);

  // Fetch the full group list once logged in, to know what the user can still join.
  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    fetchAccessGroups()
      .then(({ groups }) => {
        if (active) setAllGroups(groups || []);
      })
      .catch(() => {
        if (active) setAllGroups([]);
      });
    return () => {
      active = false;
    };
  }, [isAuthenticated]);

  const joinableGroups = allGroups.filter((group) => !user?.groups?.includes(group.name));

  const closeMenu = () => {
    setMenuOpen(false);
    setDropdownOpen(false);
  };

  const openAuth = (view) => {
    closeMenu();
    onOpenAuth(view);
  };

  const handleLogout = () => {
    closeMenu();
    logout();
  };

  return (
    <nav className={`nav${scrolled ? ' scrolled' : ''}`}>
      <Link className='nav-logo' to={'/'}>Fleshlight VIP </Link>

      <button
        className={`nav-hamburger${menuOpen ? ' open' : ''}`}
        type="button"
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}
      >
        <span />
        <span />
        <span />
      </button>

      {isAuthenticated ? (
        <div className={`nav-user${menuOpen ? ' open' : ''}`}>
          <span className="nav-username">{user.name || user.email}</span>
          {(user.collections?.length > 0 || joinableGroups.length > 0) && (
            <div className={`nav-dropdown${dropdownOpen ? ' open' : ''}`}>
              <button
                className="nav-btn nav-btn-faves"
                type="button"
                aria-expanded={dropdownOpen}
                onClick={() => setDropdownOpen((open) => !open)}
              >
                &#9733; Collections
              </button>
              {dropdownOpen && (
                <div className="nav-dropdown-menu">
                  {user.collections?.map((collection) => (
                    <Link
                      key={collection.id}
                      to={`/collections/${encodeURIComponent(collection.title)}`}
                      onClick={closeMenu}
                    >
                      {collection.title}
                    </Link>
                  ))}
                  {joinableGroups.map((group) => (
                    <div className="nav-dropdown-join-item" key={group.id}>
                      <a
                        href={group.join_url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={closeMenu}
                      >
                        Join {group.name}
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <button className="nav-btn" type="button" onClick={handleLogout}>
            Log Out
          </button>
        </div>
      ) : (
        <div className={`nav-auth${menuOpen ? ' open' : ''}`}>
          <button className="nav-btn" type="button" onClick={() => openAuth('login')}>
            Log In
          </button>
          <button
            className="nav-btn nav-btn-primary"
            type="button"
            onClick={() => openAuth('signup')}
          >
            Sign Up
          </button>
        </div>
      )}
    </nav>
  );
}
